// Shared page-chrome behaviour for LandingPage.vue and SubpageShell.vue:
//   - toggles the cream `viluu-landing-body` class on <body>
//   - soft fade-out when navigating to another internal page
//   - bfcache restore detection (so the page-in animation is skipped)
//   - opens a <details> deep-linked via the URL hash (FAQ anchors)
//   - scroll-reveal IntersectionObserver for `.reveal` descendants
//
// Consumers expose `isLeaving` / `fromBfcache` data flags (the mixin provides
// them) and bind them on their root element to drive the transition classes.
// The component's root must be the `.viluu-landing` wrapper that contains the
// `.reveal` elements — the observer scans `this.$el`.
export const landingChromeMixin = {
  data() {
    return {
      isLeaving: false,
      fromBfcache: false,
      revealObserver: null,
    };
  },
  mounted() {
    // We can't put these rules on :root because they would collide with the
    // dashboard/auth-card pages that share the same layout.
    document.body.classList.add('viluu-landing-body');

    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('click', this.onAnchorClick);
    window.addEventListener('hashchange', this.openDetailsFromHash);
    this.openDetailsFromHash();
    this.setupRevealObserver();
  },
  beforeDestroy() {
    document.body.classList.remove('viluu-landing-body');
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('click', this.onAnchorClick);
    window.removeEventListener('hashchange', this.openDetailsFromHash);
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }
  },
  methods: {
    onPageShow(e) {
      this.isLeaving = false;
      if (e.persisted) {
        this.fromBfcache = true;
      }
    },
    onAnchorClick(e) {
      if (e.defaultPrevented) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const a = e.target.closest('a');
      if (!a) {
        return;
      }
      const href = a.getAttribute('href');
      if (!href) {
        return;
      }
      if (/^(#|mailto:|tel:|javascript:)/.test(href)) {
        return;
      }
      if (a.target && a.target !== '' && a.target !== '_self') {
        return;
      }
      let url;
      try {
        url = new URL(a.href, location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin) {
        return;
      }
      if (url.pathname === location.pathname && url.search === location.search) {
        return;
      }
      e.preventDefault();
      this.isLeaving = true;
      setTimeout(() => {
        window.location.href = a.href;
      }, 150);
    },
    openDetailsFromHash() {
      const id = (location.hash || '').slice(1);
      if (!id) {
        return;
      }
      const el = document.getElementById(id);
      if (el && el.tagName === 'DETAILS') {
        el.open = true;
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }
    },
    setupRevealObserver() {
      const root = this.$el;
      if (!('IntersectionObserver' in window)) {
        root.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-in'));
        return;
      }
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              this.revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );
      this.$nextTick(() => {
        root.querySelectorAll('.reveal').forEach((el) => this.revealObserver.observe(el));
      });
    },
  },
};
